import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { tagopsService } from '../services/tagopsService';
import { useAuth } from './AuthContext';

interface InventoryItem {
  id: string;
  name: string;
  type: string;
  service: string;
  region: string;
  account: string;
  tags: Record<string, string>;
  status: string;
  lastModified: string;
  arn: string;
  creation_time: string;
  discovery_time?: string;
  username: string;
  failed_to_tag?: boolean;
}

interface InventoryData {
  resources: InventoryItem[];
  total_count: number;
  service_stats: Record<string, number>;
}

interface InventoryContextType {
  inventoryData: InventoryData | null;
  loading: boolean;
  error: string | null;
  lastFetched: Date | null;
  refreshInventory: () => Promise<void>;
  isStale: boolean;
}

const InventoryContext = createContext<InventoryContextType | undefined>(undefined);

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

// Cache key for localStorage
const CACHE_KEY = 'tagops_inventory_cache';

interface CachedData {
  data: InventoryData;
  timestamp: number;
}

export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (context === undefined) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
};

interface InventoryProviderProps {
  children: React.ReactNode;
}

export const InventoryProvider: React.FC<InventoryProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [inventoryData, setInventoryData] = useState<InventoryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  // Check if data is stale (older than cache duration)
  const isStale = lastFetched ? (Date.now() - lastFetched.getTime()) > CACHE_DURATION : true;

  // Load data from cache on mount
  useEffect(() => {
    const loadFromCache = () => {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp }: CachedData = JSON.parse(cached);
          const cacheAge = Date.now() - timestamp;
          
          if (cacheAge < CACHE_DURATION) {
            // Cache is still valid
            setInventoryData(data);
            setLastFetched(new Date(timestamp));
            setError(null);
          } else {
            // Cache is stale, remove it and clear state
            localStorage.removeItem(CACHE_KEY);
            setInventoryData(null);
            setLastFetched(null);
            setError(null);
          }
        } else {
          // No cache, clear state
          setInventoryData(null);
          setLastFetched(null);
          setError(null);
        }
      } catch (err) {
        // Invalid cache data, remove it and clear state
        localStorage.removeItem(CACHE_KEY);
        setInventoryData(null);
        setLastFetched(null);
        setError(null);
      }
    };

    loadFromCache();
  }, []);

  // Track user activity for smart refresh
  useEffect(() => {
    const updateActivity = () => {
      localStorage.setItem('tagops_last_activity', Date.now().toString());
    };

    // Track various user activities
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    // Initial activity timestamp
    updateActivity();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, []);

  // Save data to cache
  const saveToCache = useCallback((data: InventoryData) => {
    try {
      const cacheData: CachedData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      // localStorage might be full or unavailable, ignore
    }
  }, []);

  // Fetch inventory data from API
  const fetchInventoryData = useCallback(async (): Promise<InventoryData> => {
    // Check if we have authentication tokens before making API calls
    const idToken = localStorage.getItem('id_token');
    const accessToken = localStorage.getItem('access_token');
    
    if (!idToken && !accessToken) {
      throw new Error('No authentication tokens found');
    }

    const response = await tagopsService.getInventory();
    return response;
  }, []);

  // Refresh inventory data (force fetch from API)
  const refreshInventory = useCallback(async (isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    setError(null);
    
    try {
      const data = await fetchInventoryData();
      setInventoryData(data);
      setLastFetched(new Date());
      saveToCache(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch inventory data';
      
      // Check if it's an authentication error
      if (errorMessage.includes('401') || errorMessage.includes('403') || errorMessage.includes('Unauthorized')) {
        // Session expired, clear cache and don't show error for background refresh
        if (!isBackgroundRefresh) {
          setError('Session expired. Please refresh the page.');
        }
        localStorage.removeItem(CACHE_KEY);
        setInventoryData(null);
        setLastFetched(null);
      } else {
        setError(errorMessage);
      }
      
      if (!isBackgroundRefresh) {
        throw err;
      }
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  }, [fetchInventoryData, saveToCache]);

  // Initial fetch if no valid cache, but only when user is authenticated
  useEffect(() => {
    if (user && !inventoryData && !loading && !error) {
      // No data, not loading, and no error - fetch initial data
      refreshInventory().catch(() => {
        // Error handling is done in refreshInventory
      });
    }
  }, [user, inventoryData, loading, error, refreshInventory]);

  // Auto-refresh if data is stale and we have cached data
  useEffect(() => {
    if (inventoryData && isStale && !loading) {
      // Only refresh if user is active and tab is visible
      const lastActivity = localStorage.getItem('tagops_last_activity');
      const now = Date.now();
      const activityThreshold = 10 * 60 * 1000; // 10 minutes
      const isTabVisible = !document.hidden;
      
      if (isTabVisible && (!lastActivity || (now - parseInt(lastActivity)) < activityThreshold)) {
        // User is active and tab is visible, refresh in background
        refreshInventory(true).catch(() => {
          // Ignore errors for background refresh
        });
      }
    }
  }, [inventoryData, isStale, loading, refreshInventory]);

  const value: InventoryContextType = {
    inventoryData,
    loading,
    error,
    lastFetched,
    refreshInventory,
    isStale
  };

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
};
